import React, { useMemo, useRef, ReactNode } from 'react'
import { VariableSizeList as VirtualList } from 'react-window'
import Text from '../../text'
import { useLocalFilteredOptions } from '../hooks/useLocalFilteredOptions'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const DEFAULT_ITEM_HEIGHT = 36
const ITEM_HEIGHT_WITH_DESCRIPTION = 56
const MAX_VIRTUAL_LIST_HEIGHT = 340

// Pin selected options to the top only when the list is large enough to warrant it
const PIN_SELECTED_OPTIONS_THRESHOLD = 100

export interface VirtualizedOption {
  label: string | ReactNode
  value: unknown
  description?: unknown
}

export interface VirtualizedOptionListProps<T extends VirtualizedOption> {
  options: T[]
  localFilterable?: boolean
  localFilterPlaceholder?: string
  localFilterMinLength?: number
  localFilterMatchMode?: 'contains' | 'startsWith'
  initialSelectedValues?: ReadonlyArray<unknown>
  itemHeight?: number
  itemHeightWithDescription?: number
  maxHeight?: number
  elmId: string
  renderItem: (
    option: T,
    index: number,
    style?: React.CSSProperties
  ) => React.ReactNode
  getRowTitle?: (option: T) => string | undefined
  containerClassName?: string
}

export function VirtualizedOptionList<T extends VirtualizedOption>({
  options,
  localFilterable,
  localFilterPlaceholder,
  localFilterMinLength,
  localFilterMatchMode,
  initialSelectedValues,
  itemHeight = DEFAULT_ITEM_HEIGHT,
  itemHeightWithDescription = ITEM_HEIGHT_WITH_DESCRIPTION,
  maxHeight = MAX_VIRTUAL_LIST_HEIGHT,
  elmId,
  renderItem,
  getRowTitle,
  containerClassName,
}: VirtualizedOptionListProps<T>): React.ReactElement {
  // Float selected options to the top on mount so users don't have to scroll
  // to find them. Sort order is stable during interaction — items don't jump
  // around as the user checks/unchecks. Re-sorts on next open (remount).
  const initialValueRef = useRef(initialSelectedValues)
  const sortedOptions = useMemo(() => {
    const initialValue = initialValueRef.current
    if (
      !localFilterable ||
      !initialValue?.length ||
      options.length <= PIN_SELECTED_OPTIONS_THRESHOLD
    ) {
      return options
    }
    const selectedSet = new Set(
      initialValue.flatMap(v => (Array.isArray(v) ? v : [v]))
    )
    const selected: T[] = []
    const unselected: T[] = []
    for (const opt of options) {
      const vals = Array.isArray(opt.value) ? opt.value : [opt.value]
      if (vals.some(v => selectedSet.has(v))) {
        selected.push(opt)
      } else {
        unselected.push(opt)
      }
    }
    return [...selected, ...unselected]
  }, [options, localFilterable])

  const { filterText, setFilterText, filteredOptions, hasNoResults } =
    useLocalFilteredOptions({
      options: sortedOptions,
      enabled: localFilterable,
      minLength: localFilterMinLength,
      matchMode: localFilterMatchMode,
    })

  const getItemSize = (index: number): number =>
    filteredOptions[index]?.description ? itemHeightWithDescription : itemHeight

  const virtualListHeight = Math.min(
    filteredOptions.reduce(
      (sum, opt) =>
        sum + (opt.description ? itemHeightWithDescription : itemHeight),
      0
    ),
    maxHeight
  )

  const defaultRowTitle = (option: T): string | undefined => {
    const labelText =
      typeof option.label === 'string' ? option.label : String(option.value)
    if (typeof option.description === 'string') {
      return `${labelText} — ${option.description}`
    }
    return labelText
  }

  const resolveRowTitle = getRowTitle ?? defaultRowTitle

  return (
    <div className={cx({ 'local-filterable': localFilterable })}>
      {localFilterable && (
        <div className={cx('local-filter-input')}>
          <input
            type='text'
            placeholder={localFilterPlaceholder || 'Filter options...'}
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            data-elm-id={`${elmId}_local_filter`}
          />
        </div>
      )}
      {hasNoResults ? (
        <Text color='gray' size='small'>
          No matching options
        </Text>
      ) : (
        <div className={cx(containerClassName)}>
          <VirtualList
            height={virtualListHeight}
            itemCount={filteredOptions.length}
            itemSize={getItemSize}
            width='100%'
          >
            {({ index, style }) => {
              const option = filteredOptions[index]
              return (
                <div
                  style={style}
                  key={String(option.value)}
                  className={cx('virtual-row')}
                  title={resolveRowTitle(option)}
                >
                  {renderItem(option, index, style)}
                </div>
              )
            }}
          </VirtualList>
        </div>
      )}
    </div>
  )
}

export default VirtualizedOptionList
